/**
 * Track tool — the conductive run, and the plain drawn line.
 *
 * One implementation for both, because in CIRCAD they are the same primitive: a
 * `Line::` through a chain of corners. They are not the same *command*, and the
 * difference is the whole of {@link TrackTool.wire}:
 *
 *   Place Signal Wire (`PW`) is conductive. It belongs on the signals layer of
 *   a schematic or copper on a board, whatever layer happened to be active; it
 *   is one fixed width, because a wire's thickness carries no meaning and the
 *   original states one; it adopts the net of whatever it starts on and stubs
 *   out to off-grid pad centres so the copper actually lands.
 *
 *   Place Line (`PL`, and Ortho-Line `PO`) is drawing. It goes on the layer the
 *   user has chosen and at the width shown in the status bar — which is what
 *   that field is for — and carries no signal, because a line on the symbols
 *   layer is a symbol, not a connection.
 *
 * Sharing one tool had Place Line dragging the active layer onto signals and
 * taking the wire's width, so there was no way to draw on a schematic at all
 * except by connecting things.
 *
 * Click to start, click for each corner, double-click or Enter to finish,
 * Esc to abandon. Backspace removes the last corner.
 *
 * Pads that do not sit on the grid — common in imported and metric footprints —
 * would otherwise drag the whole run off-grid to reach their centre. Instead the
 * routed corners stay on the grid and orthogonal, and a short *stub* segment
 * joins each corner to its pad's exact centre, so the copper still lands on the
 * pad (and connects) while the trace itself reads straight.
 */

import type { Entity, LineEntity } from '@sircad/core/model.ts';
import { LayerType, newId } from '@sircad/core/model.ts';
import type { SnapResult } from '@sircad/core/geom/snap.ts';
import type { Point } from '../render/viewport.ts';
import { orthoConstrain, snap } from '../render/viewport.ts';
import type { Tool, ToolContext } from './tool.ts';
import { hitTest } from './hit.ts';

/**
 * The one width a schematic wire is ever drawn at, in inches.
 *
 * Ten mil, from the original's own description of Place Signal Wire and from
 * every signal-layer segment in the sample designs.
 */
export const SCHEMATIC_WIRE_WIDTH = 0.01;

/** A pad/vertex snap label for the status bar, or null for a grid point. */
function describeSnap(hit: SnapResult): string | null {
  if (hit.kind !== 'pad') return null;
  const ref = hit.ref ? `${hit.ref}-` : '';
  const sig = hit.signal ? ` (${hit.signal})` : '';
  return `${ref}${hit.pin ?? 'pad'}${sig}`;
}

/**
 * True when a point lies on an orthogonal or 45° axis from `from`.
 * Tolerance is one grid step so a slightly-off pad still counts as on-axis.
 */
function onAxis(from: Point, to: { x: number; y: number }, grid: number): boolean {
  const dx = Math.abs(to.x - from.x);
  const dy = Math.abs(to.y - from.y);
  const tol = Math.max(grid, 1e-6);
  return dx < tol || dy < tol || Math.abs(dx - dy) < tol;
}

export class TrackTool implements Tool {
  readonly id: string;
  readonly label: string;
  readonly key: string;
  readonly hint =
    'Click or Enter to place points · arrow keys nudge the crosshair · '
    + 'double-click or a repeated corner finishes · Esc abandons the run';
  readonly cursor = 'crosshair';

  /** Conductive, or a line someone is drawing. Set once, at construction. */
  private readonly wire: boolean;

  constructor(wire = true) {
    this.wire = wire;
    this.id = wire ? 'track' : 'line';
    this.label = wire ? 'Track' : 'Line';
    this.key = wire ? 'w' : 'l';
  }

  /**
   * A wire belongs on a signal layer, a trace on copper — neither on silk,
   * symbols or the pad master, which is where the previously selected layer
   * would often have put it.
   *
   * A drawn line names no types at all, so the active layer is left exactly
   * where the user put it. That is the point of the command.
   */
  get layerTypes(): Partial<Record<'sch' | 'pcb', readonly LayerType[]>> | undefined {
    if (!this.wire) return undefined;
    return {
      sch: [LayerType.Schematic] as const,
      pcb: [LayerType.Copper, LayerType.Plane] as const,
    };
  }

  /** Confirmed corners, in document space — always on the grid. */
  private points: Point[] = [];
  /** Exact pad centre a corner should stub out to, or null. Parallel to points. */
  private pads: (Point | null)[] = [];
  /**
   * The working layer as each corner was laid. Parallel to points.
   *
   * A run can cross layers: drop a via and carry on, and the copper before it
   * belongs to one layer and the copper after it to another. The layer was
   * read once, at the end, and written onto every segment — so changing layer
   * mid-run took the whole run with it, back to the first corner. The original
   * cannot do that because it writes each segment into the file as the corner
   * is laid, reading the layer at that moment — `fladdline(prf, lyr, …)` in
   * `placeline`. This is that, kept until the run is committed.
   */
  private layers: number[] = [];
  /** Pad centre for the corner the constrain step last produced. */
  private pendingPad: Point | null = null;
  /** Signal name adopted from whatever the track started on. */
  private signal: string | undefined;
  /** What the cursor is currently snapped to, for the status bar. */
  private snappedTo: string | null = null;
  /** Whether the status bar is currently showing a snap message of ours. */
  private showingSnap = false;

  onPointerDown(p: Point, ctx: ToolContext): void {
    if (this.points.length === 0 && this.wire) {
      // Starting on existing geometry adopts its net, which is how the
      // original propagates signal names as you draw. Ask the snap at the
      // cursor — it finds the exact object, before we grid-snap the corner.
      //
      // A drawn line does none of this: it is not on a net, so adopting one
      // would put a signal name on a symbol outline and join it to the
      // circuit.
      const start = ctx.snap(p.x, p.y);
      this.signal = start.signal
        ?? (() => { const h = hitTest(ctx.doc(), p.x, p.y, ctx.grid || 0.01);
                    return h && 'signal' in h ? h.signal : undefined; })();
      if (this.signal) ctx.setMessage(`Drawing on net ${this.signal}`);

      // Route from a surface pad on the pad's own layer. An SMT pad lives on
      // one copper layer, so a trace on any other layer would never connect to
      // it — the ratline would stay no matter how carefully you drew.
      if (start.kind === 'pad' && start.padSurface && start.padLayer !== undefined
          && start.padLayer !== ctx.activeLayer) {
        ctx.setActiveLayer(start.padLayer);
        ctx.setMessage(`Routing on ${ctx.doc().layers[start.padLayer]?.name ?? start.padLayer}`);
      }
    }

    const at = this.constrain(p, ctx);

    // Putting a corner on top of the last one ends the run.
    //
    // Escape abandons now, as it does everywhere else, so there has to be some
    // other way to say "that is the end of it" — and from the keyboard, where
    // there is no double click, this is it: Enter to place the last corner,
    // Enter again to finish. With a mouse the same gesture is a double click,
    // which is what a double click already was.
    const last = this.points[this.points.length - 1];
    if (last && Math.abs(last.x - at.x) < 1e-9 && Math.abs(last.y - at.y) < 1e-9) {
      this.finish(ctx);
      return;
    }

    this.points.push(at);
    this.pads.push(this.pendingPad);
    this.layers.push(ctx.activeLayer);
  }

  /**
   * Lay the corner a via needs, without the rule that ends the run.
   *
   * Stepping the layer mid-route drops a via and carries on, and the via has to
   * have a vertex under it: without one the copper crosses it rather than ending
   * on it, and the whole span belongs to a single layer. But a corner laid on
   * top of the last one is how a run is *finished*, and the cursor is very often
   * still sitting on the corner just clicked - so going through the ordinary
   * click ended the route instead of breaking it.
   *
   * So this one adds a corner only where there is not one already, and never
   * finishes. Nothing to do before the run has started.
   */
  viaCorner(p: Point, ctx: ToolContext): void {
    if (this.points.length === 0) return;
    const at = this.constrain(p, ctx);
    const last = this.points[this.points.length - 1]!;
    if (Math.abs(last.x - at.x) < 1e-9 && Math.abs(last.y - at.y) < 1e-9) return;
    this.points.push(at);
    this.pads.push(this.pendingPad);
    this.layers.push(ctx.activeLayer);
  }

  onPointerMove(p: Point, ctx: ToolContext): void {
    const at = this.constrain(p, ctx);

    // Say what the cursor jumped to — moving it somewhere the user did not
    // click is only acceptable if they can see why. Clear it again on the way
    // out, or a stale "Snap to …" claims a connection that is not being made.
    if (this.snappedTo) {
      ctx.setMessage(`Snap to ${this.snappedTo}`);
      this.showingSnap = true;
    } else if (this.showingSnap) {
      ctx.setMessage(this.signal ? `Drawing on net ${this.signal}` : this.hint);
      this.showingSnap = false;
    }

    if (this.points.length === 0) { ctx.redraw(); return; }
    ctx.preview.length = 0;
    const segs = this.buildSegments([...this.points, at], [...this.pads, this.pendingPad], ctx);
    for (const seg of segs) ctx.preview.push(seg);
    ctx.redraw();
  }

  onDoubleClick(_p: Point, ctx: ToolContext): void {
    this.finish(ctx);
  }

  onKey(ev: KeyboardEvent, ctx: ToolContext): boolean {
    if (this.points.length === 0) return false;

    // Enter is handled by the shell, which places a corner at the crosshair;
    // pressing it twice in the same place finishes the run. Escape abandons it.
    // Backspace undoes the last corner.
    if (ev.key === 'Backspace') {
      this.points.pop();
      this.pads.pop();
      if (this.points.length === 0) this.reset(ctx);
      ctx.redraw();
      return true;
    }
    return false;
  }

  cancel(ctx: ToolContext): void {
    this.reset(ctx);
    ctx.redraw();
  }

  onDeactivate(ctx: ToolContext): void {
    this.reset(ctx);
  }

  // -------------------------------------------------------------------------

  /**
   * Where the next corner should go, on the grid.
   *
   * The first corner snaps freely, to pick up a pad and its net. After that
   * ortho dominates: the segment is constrained to 90°/45° first, and a snap is
   * only accepted when the target lies on that axis — otherwise a nearby pad
   * would drag a straight run off to one side.
   *
   * A pad hit does not return the pad's centre but the nearest on-grid point on
   * the (constrained) path; the exact centre is stashed in `pendingPad` so a
   * stub can reach it. This is what keeps the run straight while the copper
   * still lands on an off-grid pad.
   */
  private constrain(p: Point, ctx: ToolContext): Point {
    this.pendingPad = null;
    const last = this.points[this.points.length - 1];

    // First corner, or ortho off: free snap.
    if (!last || !ctx.ortho) {
      const hit = ctx.snap(p.x, p.y);
      this.snappedTo = describeSnap(hit);
      if (hit.kind === 'pad') return this.gridStub(hit, snap(hit.x, ctx.grid), snap(hit.y, ctx.grid), ctx);
      return { x: hit.x, y: hit.y };
    }

    // Hold the run to whatever directions the ortho mode allows, measured
    // from the last corner.
    const axis = orthoConstrain(last.x, last.y, p.x, p.y, ctx.ortho);
    const hit = ctx.snap(axis.x, axis.y);
    if (hit.kind !== 'grid' && onAxis(last, hit, ctx.grid)) {
      this.snappedTo = describeSnap(hit);
      // Land the corner on the grid point of the constrained axis (so the run
      // stays orthogonal), and stub out to the pad centre from there.
      if (hit.kind === 'pad') return this.gridStub(hit, snap(axis.x, ctx.grid), snap(axis.y, ctx.grid), ctx);
      return { x: hit.x, y: hit.y };
    }

    this.snappedTo = null;
    return { x: snap(axis.x, ctx.grid), y: snap(axis.y, ctx.grid) };
  }

  /**
   * For a pad snap: if the pad's centre is off the grid, return the on-grid
   * corner `(gx,gy)` and remember the centre for a stub; if it is already on the
   * grid, connect to it directly.
   */
  private gridStub(hit: SnapResult, gx: number, gy: number, ctx: ToolContext): Point {
    const grid = ctx.grid || 0;
    const off = Math.hypot(hit.x - gx, hit.y - gy);
    if (grid > 0 && off > grid * 0.02) {
      // A stub exists so copper reaches a pad it must connect to. A drawn line
      // connects to nothing, so it takes the corner and leaves the pad alone.
      if (this.wire) this.pendingPad = { x: hit.x, y: hit.y };
      return { x: gx, y: gy };
    }
    return { x: hit.x, y: hit.y };
  }

  /**
   * How thick to draw.
   *
   * A schematic wire is one fixed width and takes no setting: the original
   * says "a 10-mil thick ortho-line on the signals layer", and 16,218 of the
   * 16,218 signal-layer segments across the 105 sample schematics are exactly
   * that. Its thickness carries no meaning, so offering a choice would only
   * let a drawing disagree with every other drawing.
   *
   * Everything else — a drawn line, and a trace on a board, where width is a
   * current rating and very much does mean something — takes the width shown
   * in the status bar.
   */
  /** A run with a corner down is in progress. */
  busy(): boolean {
    return this.points.length > 0;
  }

  private width(ctx: ToolContext): number {
    const d = ctx.doc();
    if (this.wire && d.kind === 'sch') return SCHEMATIC_WIRE_WIDTH;
    return d.defaults.line?.width ?? (d.kind === 'sch' ? 0.01 : 0.012);
  }

  /**
   * The polyline through the corners, plus a stub from any corner to its
   * off-grid pad centre. The stub carries the same layer, width and signal, so
   * the copper reaches the pad and the netlist sees the connection.
   */
  private buildSegments(pts: Point[], pads: (Point | null)[], ctx: ToolContext): LineEntity[] {
    const out: LineEntity[] = [];
    const width = this.width(ctx);
    const seg = (a: Point, b: Point, layer: number): void => {
      if (a.x === b.x && a.y === b.y) return;
      const e: LineEntity = {
        kind: 'line', id: newId(), layer, width,
        x1: a.x, y1: a.y, x2: b.x, y2: b.y,
      };
      if (this.signal) e.signal = this.signal;
      out.push(e);
    };
    // A segment belongs to the layer that was working when its *far* corner was
    // laid, which is the one you were on while drawing it. Drop a via and change
    // layer and the copper already down stays where it is; only what comes after
    // moves. The fallback is the live layer, for a run laid before this was
    // recorded and for the closing stub.
    const layerAt = (i: number): number => this.layers[i] ?? ctx.activeLayer;
    for (let i = 0; i < pts.length - 1; i++) seg(pts[i]!, pts[i + 1]!, layerAt(i + 1));
    // A stub to a pad belongs with the corner it grows out of.
    for (let i = 0; i < pts.length; i++) {
      const pad = pads[i];
      if (pad) seg(pts[i]!, pad, layerAt(i));
    }
    return out;
  }

  finish(ctx: ToolContext): void {
    const segs = this.buildSegments(this.points, this.pads, ctx);
    if (segs.length > 0) {
      ctx.beginEdit(!this.wire ? 'Draw line'
        : ctx.doc().kind === 'sch' ? 'Draw wire' : 'Draw trace');
      const entities: Entity[] = ctx.doc().entities;
      entities.push(...segs);
      ctx.commit();
      ctx.setMessage(`${segs.length} segment${segs.length === 1 ? '' : 's'} added`);
    }
    this.reset(ctx);
    ctx.redraw();
  }

  private reset(ctx: ToolContext): void {
    this.points = [];
    this.pads = [];
    this.layers = [];
    this.pendingPad = null;
    this.signal = undefined;
    this.snappedTo = null;
    this.showingSnap = false;
    ctx.preview.length = 0;
  }
}
